// Базовый fetcher для SWR с обработкой ошибок, авторизацией и ETag
import { API_URL } from '@/lib/api';
import { getAccessToken } from '@/lib/authStorage';

export class FetchError extends Error {
  status: number;
  info: unknown;

  constructor(message: string, status: number, info?: unknown) {
    super(message);
    this.status = status;
    this.info = info;
  }
}

// ETag кэш: ключ URL -> { etag, data }
const etagCache = new Map<string, { etag: string; data: unknown }>();

// Получить ETag из кэша
function getCachedETag(url: string): string | null {
  return etagCache.get(url)?.etag ?? null;
}

// Получить данные из ETag кэша
function getCachedData<T>(url: string): T | null {
  return (etagCache.get(url)?.data as T) ?? null;
}

// Сохранить ETag и данные в кэш
function setCachedData<T>(url: string, etag: string, data: T): void {
  etagCache.set(url, { etag, data });
}

// Универсальный fetcher для SWR с поддержкой ETag (304 Not Modified)
export async function fetcher<T>(url: string): Promise<T> {
  const token = getAccessToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Добавляем If-None-Match если есть кэшированный ETag
  const cachedETag = getCachedETag(url);
  if (cachedETag) {
    headers['If-None-Match'] = cachedETag;
  }

  const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
  const res = await fetch(fullUrl, { headers, cache: 'no-store' });

  // 304 Not Modified — возвращаем кэшированные данные
  if (res.status === 304) {
    const cachedData = getCachedData<T>(url);
    if (cachedData !== null) {
      return cachedData;
    }
    // Кэш потерян — делаем полный запрос без If-None-Match
    const retryHeaders: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) retryHeaders['Authorization'] = `Bearer ${token}`;
    const retryRes = await fetch(fullUrl, { headers: retryHeaders, cache: 'no-store' });
    if (!retryRes.ok) {
      throw new FetchError(`Ошибка запроса: ${retryRes.status}`, retryRes.status, null);
    }
    const data = await retryRes.json();
    const newEtag = retryRes.headers.get('ETag');
    if (newEtag) setCachedData(url, newEtag, data);
    return data;
  }

  if (!res.ok) {
    let info: unknown;
    try {
      info = await res.json();
    } catch {
      info = null;
    }
    throw new FetchError(
      `Ошибка запроса: ${res.status}`,
      res.status,
      info
    );
  }

  const data = await res.json();
  
  // Сохраняем ETag в кэш для следующих запросов
  const etag = res.headers.get('ETag');
  if (etag) {
    setCachedData(url, etag, data);
  }

  return data;
}

// Fetcher с поддержкой POST/PUT/DELETE для мутаций
export async function mutationFetcher<T>(
  url: string,
  options: { method?: string; body?: unknown }
): Promise<T> {
  const token = getAccessToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
  const res = await fetch(fullUrl, {
    method: options.method || 'POST',
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
    cache: 'no-store',
  });

  if (!res.ok) {
    let info: unknown;
    try {
      info = await res.json();
    } catch {
      info = null;
    }
    throw new FetchError(
      `Ошибка запроса: ${res.status}`,
      res.status,
      info
    );
  }

  // Некоторые DELETE возвращают 204 без тела
  if (res.status === 204) {
    return null as T;
  }

  return res.json();
}
